<template>
  <div>
    <div class="fixed cursor-pointer z-20 py-2 px-4 bg-white text-primary md:hidden filter__jobs-filter__button"
         :class="{'is-open': isMenuOpen}"
         @click="toggleMenu">
      <p class="text-xl font-semibold">{{$tc('common.filter', 2)}}</p>
    </div>
    <div class="rounded-lg z-20 min-w-auto md:min-w-2xl bg-white job-list__filters-wrapper"
         :class="{'is-open': isMenuOpen}">
      <div class="top-0 py-0 px-4 py-4 md:px-8 md:py-4 job-list__filters-content">
        <div v-if="jobSegments.length > 0" class="block md:hidden">
          <FiltersPreview :segments="jobSegments" @change="changeSegment"/>
        </div>
        <el-collapse v-model="activeFilters">
          <el-collapse-item v-for="(group, index) in visibleSegmentGroups"
                            :key="group.id"
                            :name="index">

            <div slot="title" class="text-lg text-black md:text-lg flex items-center">
              {{group.sgName}}
              <div v-if="showBadgeCount" class="ml-2 text-sm rounded-full bg-primary w-6 h-6 flex items-center justify-center">
                <span class="text-primary-lighter font-semibold">{{selectedCount[group.sgId]}}</span>
              </div>
            </div>

            <div class="my-2" v-for="segment in group.filters" :key="segment.sId">
              <el-checkbox :value="segment.selected" @change="onSegmentSelect($event, segment.sId)">
                <div class="filter-group__label text-xs md:text-sm filter-group__count flex items-center"
                     :class="{'text-black font-medium': segment.selected}">
                  <span class="max-w-segment truncate">{{segment.sName}}</span>
                  &nbsp;<span v-if="showAvailableSegments" class="rounded px-2 ml-1 py-1x text-primary-darker bg-primary-lighter widget-filter-count_badge">{{segment.count}}</span>
                </div>
              </el-checkbox>
            </div>

            <div class="text-normal font-bold ml-6 text-black cursor-pointer filter-group__show-more"
                 v-if="group.hasShowMore"
                 @click="toggleShowMore(group)">
              <span v-show="!isActiveShowMore(group)">{{$t('common.showMore')}}</span>
              <span v-show="isActiveShowMore(group)">{{$t('common.showLess')}}</span>
              <i class="el-icon-bottom filter-group__show-more__icon"
                 :class="{'is-open': isActiveShowMore(group)}"/>
            </div>

          </el-collapse-item>
        </el-collapse>
        <div v-if="postJobLink" class="flex flex-col items-center py-4 widget-job-filters_post-job__wrapper">
          <span class="text-primary text-2xl">{{widgetConfig.JOB_POST_TEXT}}</span>
          <el-button class="flex flex-row items-center mt-2" type="secondary">
            <slot name="buttonText">
              <a :href="this.postJobLink" target="_blank" rel="noopener">{{$t('common.postJob')}}</a>
            </slot>
          </el-button>
        </div>
      </div>
    </div>
    <div v-show="isMenuOpen"
         @click="closeMenu"
         class="fixed w-full min-h-full z-10 top-0 left-0 bg-black opacity-75">
    </div>
  </div>
</template>

<script>
  import some from 'lodash/some'
  import get from 'lodash/get'
  import cloneDeep from 'lodash/cloneDeep'
  import FiltersPreview from './FiltersPreview'
  import listParamsMixin from '../../util/mixins/listParamsMixin'
  import queryParamsMixin from '../../util/mixins/queryParamsMixin'

  export default {
    name: 'job-list-filters',
    components: {
      FiltersPreview
    },
    mixins: [listParamsMixin, queryParamsMixin],
    props: {
      jobSegments: {
        type: Array,
        default: () => []
      },
      showAvailableSegments: {
        type: Boolean,
        default: true
      },
      showBadgeCount: {
        type: Boolean,
        default: false
      }
    },
    data() {
      return {
        activeFilters: [0, 1, 2],
        isMenuOpen: false,
        showMoreSegmentsMap: {},
        widgetConfig: {},
        postJobLink: false
      }
    },
    created() {
      this.widgetConfig = this.$store.getters['widgetConfig']
      const segmentIds = this.widgetConfig.SEGMENT_IDS
      const organisationId = this.widgetConfig.ORGANIZATION_ID
      this.postJobLink = this.widgetConfig.JOB_POST_LINK
      const queryIds = get(this.$route.query, "['in[segment_ids]']", '')

      if (organisationId) {
        this.onOrganisationChange(organisationId)
      }

      if (segmentIds.length > 0) {
        segmentIds.forEach(s => {
          if (!queryIds.includes(s)) {
            this.onSegmentSelect(true, s.toString())
            this.jobSegments.push(s.toString())
          }
        })
      }
    },
    computed: {
      segmentGroupFilters() {
        let allSegmentsGroup = this.$store.getters['getAllSegmentsGroup']
        let allSegments = this.$store.getters['getAllSegments']
        let jobs = this.$store.state.allJobs

        if (!allSegmentsGroup || !jobs) {
          return
        }
        return allSegmentsGroup.map(({ key }, index) => {

          const segmentGroup = allSegmentsGroup[index]

          const segments = allSegments[key].map(segment => ({
            sName: segment.value,
            sId: segment.id,
            selected: some(this.jobSegments, id => id === segment.id.toString()),
            count: get(jobs, `aggregations.segments_ids[${segment.id}]`) || 0
          }))

          return {
            sgName: segmentGroup.value,
            sgKey: segmentGroup.key,
            sgId: segmentGroup.id,
            filters: segments
          }
        })
      },
      visibleSegmentGroups() {
        if (!this.segmentGroupFilters) {
          return
        }

        let segmentGroups = this.segmentGroupFilters

        segmentGroups.forEach(group => {
          group.filters.sort((a, b) => {
            if (a.count !== b.count) {
              return b.count - a.count
            } else {
              return b.sName < a.sName ? 1 : -1
            }
          })
        })

        segmentGroups = segmentGroups.map(group => {
          let filters = group.filters
          if (!this.showMoreSegmentsMap[group.sgId]) {
            filters = group.filters.slice(0, this.widgetConfig.SEGMENT_LIMIT)
          }

          return {
            ...group,
            filters,
            hasShowMore: group.filters.length > this.widgetConfig.SEGMENT_LIMIT
          }
        })

        return segmentGroups
      },
      selectedCount() {
        return this.segmentGroupFilters.reduce((res, sg) => {
          res[sg.sgId] = sg.filters.filter(s => s.selected).length;
          return res;
        }, {});
      },
    },
    methods: {
      onSegmentSelect(value, segmentId) {
        const idStr = segmentId.toString()
        let newSegments = cloneDeep(this.jobSegments)

        if (value) {
          newSegments.push(idStr)
        } else {
          newSegments = this.jobSegments.filter(id => id !== idStr)
        }

        this.$emit('change', newSegments)
      },
      toggleMenu() {
        this.isMenuOpen = !this.isMenuOpen
      },
      changeSegment(newSegments) {
        this.$emit('change', newSegments)
      },
      closeMenu() {
        this.isMenuOpen = false
      },
      isActiveShowMore(group) {
        return this.showMoreSegmentsMap[group.sgId]
      },
      toggleShowMore(group) {
        const showMoreStatus = this.showMoreSegmentsMap[group.sgId]
        this.$set(this.showMoreSegmentsMap, group.sgId, !showMoreStatus)
      }
    },
    watch: {
      '$route.query': {
        immediate: true,
        async handler(newValue) {
          this.$emit('loading')
          await this.$store.dispatch('getJobs', newValue)
          this.$emit('loadingFinished')
        }
      }
    }
  }
</script>
<style scoped>
  .max-w-segment {
    max-width: 250px;
  }
</style>
